import * as clipboard from 'clipboard-polyfill'
import {makeAutoObservable} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Time} from 'yougile-util'
import {
  BoardShareType,
  DataObjectId,
  Hub,
  isBoardShareType,
  ShareAllowedColumns,
  SharedBoard,
  SharedBoardRecords,
  SharedBoardSettings,
  sharePermissionsToType,
  shareTypeToPermissions
} from 'yougile-model'
import {backgroundImages} from '@client/background-images'
import {BoardMessageStats} from '@client/board-message-stats'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {FileChooser} from '@common/file-chooser'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {MessageBox} from '@components/message-box'
import {Board} from '@extensions/board'
import {globals} from '@extensions/globals-base'

interface State {
  boardId: DataObjectId | undefined
  showAllBackgrounds: boolean
  links: SharedBoardRecords
  updateTimestamp: number
}

interface Column {
  id: DataObjectId
  title: string
  hasAccess: boolean
}

export class SharePopupStore {
  state: State = {
    boardId: undefined,
    showAllBackgrounds: false,
    links: {},
    updateTimestamp: Time.now()
  }

  constructor() {
    makeAutoObservable(this)
    globals.onCompanyChange(() => this.load())
    this.load()
  }

  setBoard(boardId: DataObjectId | undefined) {
    this.state.boardId = boardId
    this.state.showAllBackgrounds = false
  }

  close() {
    this.setBoard(undefined)
  }

  toggleShowAllBackgrounds() {
    this.state.showAllBackgrounds = !this.state.showAllBackgrounds
  }

  private getSharedBoardId(): string {
    const boardLinks = ObjUtil.filter(
      this.state.links,
      (_id, b) => b.boardId === this.state.boardId
    )
    const keys = ObjUtil.keys(boardLinks)
    return keys[0]
  }

  getBoard(): Hub {
    return Sync.getCurrentSandbox().getHub(this.state.boardId)
  }

  getSharedBoard(): SharedBoard {
    return this.state.links[this.getSharedBoardId()]
  }

  getLink(): string {
    const host =
      Backend.toMainUrl('') || location.protocol + '//' + location.host
    return host + '/board/' + this.getSharedBoardId()
  }

  getUserBackgrounds(): string[] {
    if (!globals.currentUserId) {
      return []
    }
    const user = Sync.getProfile().getUser(globals.currentUserId)
    if (!user) {
      return []
    }
    return [...[...user.customSkins].reverse(), ...backgroundImages]
  }

  async change(settings: SharedBoardSettings) {
    const box = Sync.getCurrentSandbox()
    if (settings.background) {
      Tracking.send(TrackingEvent.shareChangeBg)
    }
    await SharedBoardsClient.changeBoardLink(
      box.companyId,
      this.getSharedBoardId(),
      settings
    )
    await this.load()
  }

  async toggle() {
    const sharedId = this.getSharedBoardId()
    const box = Sync.getCurrentSandbox()
    if (sharedId) {
      await SharedBoardsClient.deleteBoardLink(sharedId)
    } else {
      Tracking.send(TrackingEvent.shareCreateBoard)
      await SharedBoardsClient.addBoardLink(box.companyId, this.state.boardId)
    }
    await this.load()
    const type = this.getSharedBoard()?.type
    this.setBoardMessagesStats(type)
  }

  setBoardMessagesStats(type: BoardShareType) {
    const board = this.getBoard()
    if (type === 'chat-only' || type === 'add-tasks') {
      BoardMessageStats.turnOn(board)
    } else {
      BoardMessageStats.turnOff(board)
    }
  }

  async onTypeChange(type: string) {
    if (!isBoardShareType(type)) {
      logger.error('Incorrect type', type)
      return
    }
    this.setBoardMessagesStats(type)
    await this.change({type})
  }

  get hasAddTaskAccess(): boolean {
    const sharedBoard = this.getSharedBoard()
    const permissions = shareTypeToPermissions(sharedBoard?.type)
    return permissions.addTasks
  }

  get hasChatAccess(): boolean {
    const sharedBoard = this.getSharedBoard()
    const permissions = shareTypeToPermissions(sharedBoard?.type)
    return permissions.chat
  }

  get hasAllColumnsAccess(): boolean {
    const sharedBoard = this.getSharedBoard()
    return !sharedBoard?.allowedColumns || sharedBoard.allowedColumns === 'all'
  }

  async toggleAddTaskAccess() {
    const sharedBoard = this.getSharedBoard()
    const permissions = shareTypeToPermissions(sharedBoard.type)
    permissions.addTasks = !permissions.addTasks
    await this.onTypeChange(sharePermissionsToType(permissions))
  }

  async toggleChatAccess() {
    const sharedBoard = this.getSharedBoard()
    const permissions = shareTypeToPermissions(sharedBoard.type)
    permissions.chat = !permissions.chat
    await this.onTypeChange(sharePermissionsToType(permissions))
  }

  async toggleAllColumnsAccess() {
    const sharedBoard = this.getSharedBoard()
    const board = this.getBoard()
    const allColumns: ShareAllowedColumns = {}
    for (const col of Board.listColumns(board)) {
      allColumns[col.id] = true
    }
    sharedBoard.allowedColumns =
      (sharedBoard?.allowedColumns ?? 'all') === 'all' ? allColumns : 'all'
    await this.change({allowedColumns: sharedBoard.allowedColumns})
  }

  async toggleColumn(columnId: DataObjectId) {
    const sharedBoard = this.getSharedBoard()
    if (!sharedBoard.allowedColumns || sharedBoard.allowedColumns === 'all') {
      sharedBoard.allowedColumns = {[columnId]: true}
    } else {
      if (sharedBoard.allowedColumns[columnId]) {
        delete sharedBoard.allowedColumns[columnId]
      } else {
        sharedBoard.allowedColumns[columnId] = true
      }
    }
    await this.change({allowedColumns: sharedBoard.allowedColumns})
  }

  get columns(): Map<DataObjectId, Column> {
    const colHubs = Board.listColumns(this.getBoard(), {
      sorted: true
    })
    const res: Map<DataObjectId, Column> = new Map()
    const sharedBoard = this.getSharedBoard()
    const {allowedColumns} = sharedBoard
    for (const col of colHubs) {
      res.set(col.id, {
        id: col.id,
        title: col.title,
        hasAccess:
          !allowedColumns || allowedColumns === 'all' || allowedColumns[col.id]
      })
    }
    return res
  }

  async addBg(event: React.MouseEvent) {
    if (!globals.currentUserId) {
      return
    }
    const {url} = await FileChooser.uploadImage(event)
    const user = Sync.getProfile().getUser(globals.currentUserId)
    user?.customSkins.push(url)
    user?.commit()
    this.updateOnExternalChange()
  }

  async copy() {
    return clipboard
      .writeText(this.getLink())
      .then(() => MessageBox.show(Lang.msg.permission.regLinkCopied, 'success'))
      .catch(e => logger.log('err', e))
  }

  setLinks(links: SharedBoardRecords) {
    this.state.links = links
  }

  async load() {
    const companyId = globals.getCompanyId()
    const res = await SharedBoardsClient.listSharedBoards(companyId)
    this.setLinks(res)
  }

  updateOnExternalChange() {
    this.state.updateTimestamp = Time.now()
  }
}
